import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    FileText,
    Search,
    Filter,
    Calendar,
    User,
    AlertCircle,
    CheckCircle,
    XCircle,
    Eye,
    Download,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Shield,
    Database,
    Users,
    Settings
} from "lucide-react";
import BackButton from "../../components/BackButton";

const ViewAuditLogs = () => {
    const [logs, setLogs] = useState([
        {
            id: 1,
            action: "Project Created",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 10:00:00",
            details: "Created new project 'Website Redesign' with initial budget of $25,000",
            ip: "192.168.1.100",
            status: "success",
            severity: "low"
        },
        {
            id: 2,
            action: "Team Member Added",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 11:00:00",
            details: "Added 'Alex Johnson' to project 'Website Redesign' as Lead Designer",
            ip: "192.168.1.100",
            status: "success",
            severity: "low"
        },
        {
            id: 3,
            action: "Project Status Updated",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 12:00:00",
            details: "Changed project status from 'Planning' to 'Active' for 'Website Redesign'",
            ip: "192.168.1.100",
            status: "success",
            severity: "medium"
        },
        {
            id: 4,
            action: "Budget Modified",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 13:00:00",
            details: "Updated budget for 'Mobile App V2' from $40,000 to $50,000",
            ip: "192.168.1.100",
            status: "success",
            severity: "medium"
        },
        {
            id: 5,
            action: "Task Assignment Failed",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 14:00:00",
            details: "Failed to assign task 'API Integration' to unavailable team member",
            ip: "192.168.1.100",
            status: "failed",
            severity: "high"
        },
        {
            id: 6,
            action: "Document Uploaded",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 15:00:00",
            details: "Uploaded project specification document (specs.pdf, 2.4MB)",
            ip: "192.168.1.100",
            status: "success",
            severity: "low"
        },
        {
            id: 7,
            action: "Permission Denied",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 16:00:00",
            details: "Attempted to access restricted admin settings - Access denied",
            ip: "192.168.1.100",
            status: "failed",
            severity: "high"
        },
        {
            id: 8,
            action: "Report Generated",
            user: "Project Leader",
            email: "leader@teamhub.com",
            timestamp: "2024-11-27 17:00:00",
            details: "Generated weekly progress report for all active projects",
            ip: "192.168.1.100",
            status: "success",
            severity: "low"
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedSeverity, setSelectedSeverity] = useState("all");
    const [dateRange, setDateRange] = useState("today");
    const [expandedLog, setExpandedLog] = useState(null);

    const statusOptions = [
        { value: "all", label: "All Statuses" },
        { value: "success", label: "Success" },
        { value: "failed", label: "Failed" },
        { value: "warning", label: "Warning" }
    ];

    const severityLevels = [
        { value: "all", label: "All Severities" },
        { value: "low", label: "Low", color: "bg-green-100 text-green-800" },
        { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800" },
        { value: "high", label: "High", color: "bg-red-100 text-red-800" }
    ];

    const dateRanges = [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "week", label: "Last 7 days" },
        { value: "month", label: "Last 30 days" }
    ];

    const getStatusIcon = (status) => {
        switch (status) {
            case "success": return <CheckCircle className="w-5 h-5 text-green-500" />;
            case "failed": return <XCircle className="w-5 h-5 text-red-500" />;
            default: return <AlertCircle className="w-5 h-5 text-yellow-500" />;
        }
    };

    const getActionIcon = (action) => {
        if (action.includes("Project")) return <FileText className="w-4 h-4" />;
        if (action.includes("Team")) return <Users className="w-4 h-4" />;
        if (action.includes("Budget")) return <Database className="w-4 h-4" />;
        if (action.includes("Permission")) return <Shield className="w-4 h-4" />;
        if (action.includes("Report")) return <FileText className="w-4 h-4" />;
        return <Settings className="w-4 h-4" />;
    };

    const filteredLogs = logs.filter(log => {
        const matchesSearch =
            log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.user.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = selectedStatus === "all" || log.status === selectedStatus;
        const matchesSeverity = selectedSeverity === "all" || log.severity === selectedSeverity;

        return matchesSearch && matchesStatus && matchesSeverity;
    });

    const stats = {
        total: logs.length,
        success: logs.filter(l => l.status === "success").length,
        failed: logs.filter(l => l.status === "failed").length,
        highSeverity: logs.filter(l => l.severity === "high").length
    };

    const exportLogs = () => {
        const dataStr = JSON.stringify(filteredLogs, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const refreshLogs = () => {
        // In a real app, this would fetch from API
        // For now, we'll just show a loading state
        setTimeout(() => {
            // Simulate refresh
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/project-leader/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
                                <p className="text-gray-600 mt-1">Track and review project activities</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={refreshLogs}
                                className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={exportLogs}
                                className="flex items-center px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export Logs
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Actions</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                                </div>
                                <FileText className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Successful</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.success}</p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Failed</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.failed}</p>
                                </div>
                                <XCircle className="w-8 h-8 text-red-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">High Severity</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.highSeverity}</p>
                                </div>
                                <AlertCircle className="w-8 h-8 text-orange-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search audit logs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedSeverity}
                                onChange={(e) => setSelectedSeverity(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {severityLevels.map(level => (
                                    <option key={level.value} value={level.value}>
                                        {level.label}
                                    </option>
                                ))}
                            </select>
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-5 h-5 text-gray-400" />
                                <div className="flex gap-2">
                                    {dateRanges.map(range => (
                                        <button
                                            key={range.value}
                                            onClick={() => setDateRange(range.value)}
                                            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                                dateRange === range.value
                                                    ? 'bg-primary-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {range.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logs Table */}
                <div className="card overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">Project Audit Logs</h2>
                            <div className="text-sm text-gray-600">
                                Showing {filteredLogs.length} of {logs.length} logs
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Severity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timestamp
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Details
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {filteredLogs.map((log) => (
                                <React.Fragment key={log.id}>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <div className="text-gray-600">
                                                        {getActionIcon(log.action)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{log.action}</div>
                                                    <div className="text-sm text-gray-500">{log.ip}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <User className="w-4 h-4 text-blue-600" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{log.user}</div>
                                                    <div className="text-sm text-gray-500">{log.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            severityLevels.find(l => l.value === log.severity)?.color || 'bg-gray-100 text-gray-800'
                        }`}>
                          {log.severity.charAt(0).toUpperCase() + log.severity.slice(1)}
                        </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                {getStatusIcon(log.status)}
                                                <span className={`font-medium ${
                                                    log.status === 'success' ? 'text-green-600' :
                                                        log.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                                                }`}>
                            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{log.timestamp}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                                    className="flex items-center text-primary-600 hover:text-primary-700 font-medium"
                                                >
                                                    <Eye className="w-4 h-4 mr-1" />
                                                    {expandedLog === log.id ? 'Hide' : 'View'}
                                                </button>
                                                <button
                                                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                                    className="p-1 text-gray-400 hover:text-gray-600"
                                                >
                                                    {expandedLog === log.id ? (
                                                        <ChevronUp className="w-4 h-4" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedLog === log.id && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="space-y-4"
                                                >
                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Action Details</h4>
                                                            <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                                                                {log.details}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Information</h4>
                                                            <div className="space-y-2">
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">Log ID:</span>
                                                                    <span className="font-medium text-gray-900">AUDIT-{log.id.toString().padStart(6, '0')}</span>
                                                                </div>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">IP Address:</span>
                                                                    <span className="font-medium text-gray-900">{log.ip}</span>
                                                                </div>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-600">Source:</span>
                                                                    <span className="font-medium text-gray-900">Project Management</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                                        <button className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                                            Mark as Reviewed
                                                        </button>
                                                        <button className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                                            Create Follow-up
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredLogs.length === 0 && (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                <FileText className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
                            <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                {searchTerm || selectedStatus !== 'all' || selectedSeverity !== 'all'
                                    ? "No logs match your search criteria. Try adjusting your filters."
                                    : "No audit logs have been recorded for the selected time period."
                                }
                            </p>
                            {(searchTerm || selectedStatus !== 'all' || selectedSeverity !== 'all') && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedStatus('all');
                                        setSelectedSeverity('all');
                                    }}
                                    className="btn-primary"
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {filteredLogs.length > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    Page <span className="font-medium">1</span> of{' '}
                                    <span className="font-medium">1</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                        Previous
                                    </button>
                                    <button className="px-3 py-1.5 border border-gray-300 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                        1
                                    </button>
                                    <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Activity Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                >
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Activity Distribution */}
                        <div className="card">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Activity Distribution</h2>
                            <div className="space-y-4">
                                {['Project Actions', 'Team Management', 'Budget Changes', 'Security Events'].map((category, index) => (
                                    <div key={category} className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">{category}</span>
                                            <span className="font-medium text-gray-900">
                        {Math.floor(Math.random() * 20) + 5} actions
                      </span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${
                                                    index === 0 ? 'bg-blue-500' :
                                                        index === 1 ? 'bg-green-500' :
                                                            index === 2 ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${Math.floor(Math.random() * 60) + 30}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent High Priority */}
                        <div className="card">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Recent High Priority</h2>
                            <div className="space-y-4">
                                {logs.filter(l => l.severity === 'high').slice(0, 3).map(log => (
                                    <div key={log.id} className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="font-medium text-gray-900">{log.action}</h4>
                                            <p className="text-sm text-gray-600 mt-1">{log.details.substring(0, 60)}...</p>
                                            <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                                                <span>{log.timestamp}</span>
                                                <button className="text-red-600 hover:text-red-700 font-medium">
                                                    Review
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ViewAuditLogs;